import { useEffect, useMemo, useRef, useState } from "react";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import api from "@/utils/axios";
import i18n from "@/i18n";

import cls from "./WalletETransfer.module.scss";

import coinIcon from "@/assets/UI/coin.webp";
import mrImg from "@/assets/UI/memberCreate/q20Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q20MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q20Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q20MrsM.webp";

const PROCESSED_SUBPATH = "images/poster.webp";

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const refillOptions = [
  {
    id: "starter",
    amountCad: 15,
    baseNoty: 150,
    bonusNoty: 0,
    totalNoty: 150,
    bonusPercent: 0,
    badge: "Classic",
  },
  {
    id: "boost",
    amountCad: 50,
    baseNoty: 500,
    bonusNoty: 100,
    totalNoty: 600,
    bonusPercent: 20,
    badge: "Popular",
  },
  {
    id: "max",
    amountCad: 100,
    baseNoty: 1000,
    bonusNoty: 800,
    totalNoty: 1800,
    bonusPercent: 80,
    badge: "Best Deal",
    isBest: true,
  },
];

const formatNoty = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatCad = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
  });

function filterPaymentOptionsByLanguage(options, lang) {
  return (options || []).filter((opt) => {
    const name = String(opt.name || "").toLowerCase();
    if (name === "si") return lang === "si";
    if (name === "ta") return lang === "ta";
    if (name === "hi") return lang === "hi";
    return true;
  });
}

function toInstructionSteps(raw = "") {
  const text = String(raw || "").trim();
  if (!text) return [];

  // Split by new lines first; if admin stores as one long line, also split on " 01." " 02." patterns
  const byNewLine = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (byNewLine.length >= 2) return byNewLine;

  // Try to split by numbered patterns (01., 1., 01) etc.
  const parts = text
    .split(/(?:^|\s)(?=(?:\d{1,2}\s*[.)-]))/g)
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length >= 2) return parts;

  // Fallback: treat whole text as one step
  return [text];
}

function pad2(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "00";
  return String(v).padStart(2, "0");
}

export default function WalletETransfer() {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [selectedPaymentOptionId, setSelectedPaymentOptionId] = useState("");
  const [selectedAmountCad, setSelectedAmountCad] = useState(100);
  const [transferDate, setTransferDate] = useState("");

  const [file, setFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [proofImageKey, setProofImageKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const currentLang = i18n.language || "en";
  const visibleOptions = useMemo(
    () => filterPaymentOptionsByLanguage(paymentOptions, currentLang),
    [paymentOptions, currentLang],
  );

  const selectedRefill = useMemo(
    () =>
      refillOptions.find((option) => option.amountCad === selectedAmountCad),
    [selectedAmountCad],
  );

  const selectedPaymentOption = useMemo(() => {
    return (
      visibleOptions.find((o) => o._id === selectedPaymentOptionId) || null
    );
  }, [visibleOptions, selectedPaymentOptionId]);

  const instructionSteps = useMemo(() => {
    const raw =
      selectedPaymentOption?.instruction ||
      selectedPaymentOption?.description ||
      "";
    return toInstructionSteps(raw);
  }, [selectedPaymentOption]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setLoadingOptions(true);
        const { data } = await api.get("/v1/member-create/payment/options");
        if (!isMounted) return;

        const list = Array.isArray(data?.paymentOptions)
          ? data.paymentOptions
          : [];
        setPaymentOptions(list);

        // Auto-select first visible option once loaded (nice UX)
        setTimeout(() => {
          if (!isMounted) return;
          const nextVisible = filterPaymentOptionsByLanguage(list, currentLang);
          if (!selectedPaymentOptionId && nextVisible?.[0]?._id) {
            setSelectedPaymentOptionId(nextVisible[0]._id);
          }
        }, 0);
      } catch (err) {
        console.error("load payment options failed:", err);
        toast.error(
          i18n.t("wallet.refill.loadOptionsError", {
            defaultValue: "Couldn’t load payment options. Please try again.",
          }),
        );
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If language changes, and selected option is not visible anymore, fallback to first.
  useEffect(() => {
    if (!visibleOptions.length) return;
    if (!selectedPaymentOptionId) return;

    const ok = visibleOptions.some((o) => o._id === selectedPaymentOptionId);
    if (!ok) setSelectedPaymentOptionId(visibleOptions[0]._id);
  }, [visibleOptions, selectedPaymentOptionId]);

  function cleanupPreview(url) {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }

  function setPickedFile(f) {
    if (!f) return;

    if (!/^image\//.test(f.type)) {
      toast.info(
        i18n.t("wallet.refill.onlyImages", {
          defaultValue: "Please choose an image file (screenshot or receipt).",
        }),
      );
      return;
    }

    setFile(f);
    cleanupPreview(localPreview);
    const url = URL.createObjectURL(f);
    setLocalPreview(url);

    setProofImageKey("");
    setProgress(0);
  }

  function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPickedFile(f);
  }

  function onDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    setPickedFile(f);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  async function uploadProofOriginal() {
    if (!file) {
      throw new Error("Missing proof image");
    }

    setUploading(true);
    setProgress(0);

    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";

      const { data } = await api.post(
        "/v1/member/wallet/e-transfer/proof/presign",
        {
          ext,
          contentType: file.type || "application/octet-stream",
        },
      );

      const payload = data?.data ?? data;
      const { uploadUrl, processedBaseKey } = payload || {};
      if (!uploadUrl || !processedBaseKey) {
        throw new Error("Invalid presign response");
      }

      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });

      const nextProofKey = `${processedBaseKey}/${PROCESSED_SUBPATH}`;
      setProofImageKey(nextProofKey);
      setProgress(100);
      return nextProofKey;
    } catch (err) {
      console.error("payment proof upload failed:", err);
      setProofImageKey("");
      setProgress(0);
      if (err && typeof err === "object") {
        err.stage = "upload";
      }
      throw err;
    } finally {
      setUploading(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();

    if (!file && !proofImageKey) {
      toast.info(
        i18n.t("wallet.refill.needImage", {
          defaultValue: "Please upload your payment proof before submitting.",
        }),
      );
      return;
    }
    if (!selectedPaymentOptionId) {
      toast.info(
        i18n.t("wallet.refill.needMethod", {
          defaultValue: "Please select which payment option you used.",
        }),
      );
      return;
    }
    if (!transferDate) {
      toast.info(
        i18n.t("wallet.refill.needDate", {
          defaultValue: "Please select the transfer date.",
        }),
      );
      return;
    }
    if (!selectedRefill) {
      toast.info(
        i18n.t("wallet.refill.needAmount", {
          defaultValue: "Please choose a refill amount.",
        }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const nextProofImageKey = proofImageKey || (await uploadProofOriginal());

      const payload = {
        paymentOptionId: selectedPaymentOptionId,
        transferDate,
        proofImageKey: nextProofImageKey,
        amountCad: selectedRefill.amountCad,
      };

      await api.post("/v1/member/wallet/e-transfer/submit", payload);

      toast.success(
        i18n.t("wallet.refill.afterSubmit", {
          defaultValue:
            "Thanks! Your wallet refill is submitted and pending admin review.",
        }),
      );

      navigate("/member/wallet", { replace: true });
    } catch (err) {
      console.error("submit wallet refill failed:", err);
      if (err?.stage === "upload") {
        toast.error(
          i18n.t("wallet.refill.uploadError", {
            defaultValue: "Upload failed. Please try again.",
          }),
        );
      } else {
        toast.error(
          i18n.t("wallet.refill.submitError", {
            defaultValue:
              "Couldn’t submit your refill. Please check your details and try again.",
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    uploading ||
    (!file && !proofImageKey) ||
    !selectedPaymentOptionId ||
    !transferDate ||
    !selectedRefill;

  return (
    <main className={cls.page}>
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <header className={cls.cardHeader}>
              <div className={cls.headerText}>
                <p className={cls.kicker}>Member Wallet</p>
                <h1 className={cls.title}>E-Transfer / Bank Transfer</h1>
                <p className={cls.subtitle}>
                  Choose a refill, send your transfer, upload proof, and submit
                  for admin approval.
                </p>
              </div>
              <div className={cls.exchangeNote}>1 CAD = 10 NOTY</div>
            </header>

            <section className={cls.refillSection}>
              <div className={cls.sectionHeader}>
                <div>
                  <h2 className={cls.sectionTitle}>Choose your refill</h2>
                  <p className={cls.sectionSub}>
                    Bigger refills may include bonus coins. Pick the bundle you
                    want.
                  </p>
                </div>

                {selectedRefill ? (
                  <div className={cls.summaryPill}>
                    {formatCad(selectedRefill.amountCad)} ={" "}
                    {formatNoty(selectedRefill.totalNoty)} NOTY
                  </div>
                ) : null}
              </div>

              <div className={cls.refillGrid}>
                {refillOptions.map((option) => {
                  const isActive = option.amountCad === selectedAmountCad;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={[
                        cls.refillCard,
                        isActive ? cls.refillCardActive : "",
                        option.isBest ? cls.refillCardBest : "",
                      ].join(" ")}
                      onClick={() => setSelectedAmountCad(option.amountCad)}
                      aria-pressed={isActive}
                    >
                      <div className={cls.refillTop}>
                        <span className={cls.refillCad}>
                          {formatCad(option.amountCad)}
                        </span>

                        <span className={cls.refillBadges}>
                          {option.isBest ? (
                            <span
                              className={`${cls.refillBadge} ${cls.badgeBest}`}
                            >
                              {option.badge}
                            </span>
                          ) : (
                            <span className={cls.refillBadge}>
                              {option.badge}
                            </span>
                          )}

                          {isActive ? (
                            <span className={cls.selectedChip}>Selected</span>
                          ) : null}
                        </span>
                      </div>

                      <div className={cls.refillTotal}>
                        <img
                          src={coinIcon}
                          alt="Noty Coin"
                          className={cls.coinIcon}
                        />
                        <span className={cls.refillNoty}>
                          {formatNoty(option.totalNoty)} NOTY
                        </span>
                      </div>

                      <div className={cls.refillMeta}>
                        Base {formatNoty(option.baseNoty)} + Bonus{" "}
                        {formatNoty(option.bonusNoty)}
                      </div>
                      <div className={cls.refillBonus}>
                        +{option.bonusPercent}% bonus
                      </div>

                      {isActive ? (
                        <div className={cls.activeGlow} aria-hidden="true" />
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {selectedRefill ? (
                <div className={cls.amountCallout}>
                  <div className={cls.amountCalloutTitle}>Important</div>
                  <p className={cls.amountCalloutText}>
                    You need to submit a payment proof of{" "}
                    <span className={cls.amountStrong}>
                      {formatCad(selectedRefill.amountCad)}
                    </span>{" "}
                    to our account.
                  </p>
                </div>
              ) : null}
            </section>

            <form className={cls.form} onSubmit={onSubmit} noValidate>
              <section className={cls.infoBlock}>
                <div className={cls.infoTitle}>Payment option</div>
                <div className={cls.infoNote}>
                  You can use,{" "}
                  <span className={cls.amountStrong}>e-transfer (Canada)</span>{" "}
                  or{" "}
                  <span className={cls.amountStrong}>
                    Wise/Remitly (International)
                  </span>
                  . Select the payment option you used, then follow the
                  instructions.
                </div>

                {loadingOptions && <div className={cls.infoNote}>Loading…</div>}

                {!loadingOptions && visibleOptions.length === 0 && (
                  <div className={cls.infoNote}>
                    No payment options are available right now. Please contact
                    support.
                  </div>
                )}

                {visibleOptions.length > 0 && (
                  <>
                    <div className={cls.fieldGroupInline}>
                      <label className={cls.fieldLabel} htmlFor="paymentMethod">
                        Payment option used
                      </label>

                      <div className={cls.selectWrap}>
                        <select
                          id="paymentMethod"
                          className={cls.select}
                          value={selectedPaymentOptionId}
                          onChange={(e) =>
                            setSelectedPaymentOptionId(e.target.value)
                          }
                        >
                          <option value="">Select a payment option</option>
                          {visibleOptions.map((opt) => (
                            <option key={opt._id} value={opt._id}>
                              {opt.title}
                            </option>
                          ))}
                        </select>
                        <span className={cls.selectArrow} aria-hidden="true" />
                      </div>

                      <div className={cls.fieldHint}>
                        Payment method: Bank Transfer
                      </div>
                    </div>

                    {selectedPaymentOption ? (
                      <div className={cls.payOptionMeta}>
                        <div className={cls.payOptionTitleRow}>
                          <div className={cls.payOptionTitle}>
                            {selectedPaymentOption.title}
                          </div>
                        </div>

                        {selectedPaymentOption.description ? (
                          <div className={cls.payOptionDesc}>
                            {selectedPaymentOption.description}
                          </div>
                        ) : null}

                        {instructionSteps.length > 0 ? (
                          <div className={cls.instructionCard}>
                            <div className={cls.instructionHead}>
                              <div className={cls.instructionLabel}>
                                Instruction
                              </div>
                            </div>

                            <ol className={cls.instructionList}>
                              {instructionSteps.map((step, idx) => (
                                <li key={`${idx}-${step.slice(0, 12)}`}>
                                  <span className={cls.stepNo}>
                                    {pad2(idx + 1)}.
                                  </span>
                                  <span className={cls.stepText}>{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </section>

              <div className={cls.fieldGroup}>
                <label className={cls.fieldLabel} htmlFor="transferDate">
                  Transfer date
                </label>
                <input
                  id="transferDate"
                  type="date"
                  className={cls.input}
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                />
                <div className={cls.fieldHint}>
                  Pick the date you made the transfer.
                </div>
              </div>

              <div className={cls.fieldGroup}>
                <label className={cls.fieldLabel}>Upload transfer proof</label>
                <div className={cls.uploadHint}>
                  Upload a screenshot of the successful transfer confirmation.
                </div>

                <div
                  className={cls.uploadBox}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                >
                  {localPreview ? (
                    <img
                      src={localPreview}
                      alt="Preview"
                      className={cls.previewImg}
                    />
                  ) : (
                    <div className={cls.uploadPlaceholder}>
                      <span>Drag your screenshot here</span>
                      <span>PNG/JPG/WebP supported</span>
                    </div>
                  )}

                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className={cls.fileInput}
                    onChange={onPickFile}
                  />
                </div>

                <div className={cls.uploadActions}>
                  <button
                    type="button"
                    className={cls.secondaryButton}
                    onClick={() => inputRef.current?.click()}
                  >
                    Choose file
                  </button>
                </div>

                <div className={cls.fieldHint}>
                  Your proof will upload when you submit the refill request.
                </div>
              </div>

              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submitButton}
                  disabled={submitDisabled}
                >
                  {uploading
                    ? `Uploading ${progress}%`
                    : submitting
                      ? "Submitting…"
                      : "Submit refill request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
